import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { distinctUntilChanged, filter, map, pipe, switchMap, withLatestFrom } from 'rxjs';

import { TenantService, UserService } from '@celum/authentication';
import { CelumPropertiesProvider } from '@celum/core/configuration';
import { isTruthy } from '@celum/core/rxjs';
import { PusherConfiguration, PusherService } from '@celum/shared/util';

import { NotificationClientConfig } from '../model/notification-client-config.model';

const USER_CHANNEL_PREFIX = 'private-server-to-user-';

export type NotificationsCenterState = {
  clientConfiguration: NotificationClientConfig;
  count: number; // The count of unread notifications received from BE
  baselineCount: number; // The last count of unread notifications at the time of opening the panel or refreshing
  localCountChanges: number; // The count of local changes (marking as read/unread)
  error: HttpErrorResponse | null;
};

export const NotificationsCenterStore = signalStore(
  withState<NotificationsCenterState>({ count: 0, baselineCount: 0, localCountChanges: 0, clientConfiguration: null, error: null }),
  withMethods((state, httpClient = inject(HttpClient)) => ({
    fetchNotificationCount: rxMethod<void>(
      switchMap(() =>
        httpClient
          .get<number>(`${CelumPropertiesProvider.properties.systemBar.notification.httpBaseAddress}/notifications/count?channel=inapp&state=unread`)
          .pipe(
            tapResponse({
              next: count => patchState(state, { count }),
              error: error => console.error('NotificationsCenterStore: error loading notification count', error)
            })
          )
      )
    ),
    loadNotificationClientConfigs: rxMethod<void>(
      pipe(
        switchMap(() => {
          const url = `${CelumPropertiesProvider.properties.systemBar.notification.httpBaseAddress}/configurations/clients`;
          return httpClient.get<NotificationClientConfig[]>(url).pipe(
            tapResponse({
              next:
                // this will not take only [0] once we'll have more than just Work notifications
                configs => patchState(state, { clientConfiguration: configs[0] }),
              error: (error: HttpErrorResponse) => patchState(state, { error })
            })
          );
        })
      )
    ),
    updateCount: (delta: number) => {
      const currentCount = state.count();
      const currentlocalCountChanges = state.localCountChanges();
      patchState(state, { localCountChanges: currentlocalCountChanges + delta, count: currentCount + delta });
    },
    setCount: (count: number) => {
      patchState(state, { count, baselineCount: count, localCountChanges: 0 });
    },
    storeCompareCount: () => {
      patchState(state, { baselineCount: state.count(), localCountChanges: 0 });
    }
  })),
  withHooks({
    onInit(store) {
      const userService = inject(UserService);
      const pusherService = inject(PusherService);
      const tenantService = inject(TenantService);

      userService.currentUser$
        .pipe(
          isTruthy(),
          map(user => user.oid),
          distinctUntilChanged(),
          takeUntilDestroyed()
        )
        .subscribe(userId => {
          const notificationConfig = CelumPropertiesProvider.properties.systemBar?.notification;
          const pusherKey = CelumPropertiesProvider.properties.websocketPusherAppKey;

          if (!pusherKey) {
            console.warn('NotificationsCenterStore: No "websocketPusherAppKey" configured in CelumProperties. WebSockets will not be enabled.');
            return;
          }

          const pusherConfig: PusherConfiguration = {
            appKey: pusherKey,
            authUrl: `${notificationConfig?.httpBaseAddress}/pusher/auth`,
            channelPrefix: USER_CHANNEL_PREFIX,
            enableLogging: false
          };

          pusherService.initialize(pusherConfig, userId);

          pusherService
            .watchPrivateUserChannel<{ count: number; tenantId: string }>('IN_APP_NOTIFICATION_COUNT_UPDATED')
            .pipe(
              takeUntilDestroyed(),
              withLatestFrom(tenantService.currentTenantId$),
              filter(([data, currentTenantId]) => data.tenantId === currentTenantId)
            )
            .subscribe(([data, _]) => {
              patchState(store, { count: data.count });
            });
        });

      store.fetchNotificationCount();

      store.loadNotificationClientConfigs();
    }
  }),
  withComputed(({ count, baselineCount, localCountChanges }) => ({
    refreshCount: computed(() => count() - (baselineCount() + localCountChanges()))
  }))
);
